"use client"

import type React from "react"
import { useState, useEffect } from "react"
import { X, Zap } from "lucide-react"
import { Button } from "../../../../components/ui/button"
import { Input } from "../../../../components/ui/input"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../../components/ui/tabs"
import type { Admin } from "../../../../lib/admin-data"
import {
  ROLE_OPTIONS, ROLE_LABELS, departments,
  PERMISSIONS, PERMISSION_GROUPS,
  ROLE_PERMISSION_PRESETS, normalizePermissions,
} from "../../../../lib/admin-data"

interface EditAdminModalProps {
  isOpen: boolean
  onClose: () => void
  onUpdate: (admin: Admin) => void
  admin: Admin | null
}

export function EditAdminModal({ isOpen, onClose, onUpdate, admin }: EditAdminModalProps) {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    role: "teacher",
    department: departments[0],
    status: "Active" as Admin["status"],
  })
  const [perms, setPerms] = useState<Record<string, boolean>>(normalizePermissions())

  useEffect(() => {
    if (admin) {
      setFormData({
        name: admin.name,
        email: admin.email,
        role: admin.role,
        department: admin.department,
        status: admin.status,
      })
      setPerms(normalizePermissions(admin.permissions))
    }
  }, [admin])

  const applyPreset = () => {
    const preset = ROLE_PERMISSION_PRESETS[formData.role] ?? ROLE_PERMISSION_PRESETS.other
    setPerms(normalizePermissions(preset))
  }

  const toggle = (key: string) => setPerms((p) => ({ ...p, [key]: !p[key] }))

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.name || !formData.email || !admin) return
    onUpdate({ ...admin, ...formData, permissions: perms })
    onClose()
  }

  const enabledCount = Object.values(perms).filter(Boolean).length

  if (!isOpen || !admin) return null

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden animate-in zoom-in-95 duration-200">
          <div className="bg-indigo-600 px-6 py-4 flex justify-between items-center shrink-0">
            <h3 className="text-lg font-bold text-white">Edit Staff Member</h3>
            <button onClick={onClose} className="text-white hover:text-gray-200 rounded-full p-1">
              <X className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
            <div className="flex-1 overflow-y-auto px-6 py-6">
              <Tabs defaultValue="basic" className="w-full">
                <TabsList className="grid w-full grid-cols-2 mb-6">
                  <TabsTrigger value="basic">Basic Information</TabsTrigger>
                  <TabsTrigger value="permissions">
                    Permissions
                    <span className="ml-1.5 text-xs bg-indigo-100 text-indigo-700 rounded-full px-1.5 py-0.5 font-semibold">
                      {enabledCount}
                    </span>
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="basic" className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Staff Name</label>
                    <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Full name" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                    <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                      <select
                        className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        value={formData.role}
                        onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                      >
                        {ROLE_OPTIONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
                      <select
                        className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        value={formData.department}
                        onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                      >
                        {departments.map((d) => <option key={d} value={d}>{d}</option>)}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <select
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value as Admin["status"] })}
                    >
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                      <option value="Suspended">Suspended</option>
                      <option value="In Review">In Review</option>
                    </select>
                  </div>
                </TabsContent>

                <TabsContent value="permissions" className="space-y-5">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-gray-500">{enabledCount} of {PERMISSIONS.length} permissions enabled</p>
                    <button
                      type="button"
                      onClick={applyPreset}
                      className="flex items-center gap-1.5 text-xs font-semibold text-indigo-600 hover:text-indigo-700 border border-indigo-200 px-3 py-1.5 rounded-lg hover:bg-indigo-50 transition-colors"
                    >
                      <Zap className="w-3.5 h-3.5" />
                      Apply {ROLE_LABELS[formData.role] ?? "Role"} Preset
                    </button>
                  </div>

                  {PERMISSION_GROUPS.map((group) => (
                    <div key={group}>
                      <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">{group}</p>
                      <div className="space-y-1.5">
                        {PERMISSIONS.filter((p) => p.group === group).map((perm) => (
                          <label
                            key={perm.key}
                            className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-all ${
                              perms[perm.key]
                                ? "border-indigo-300 bg-indigo-50"
                                : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={perms[perm.key] ?? false}
                              onChange={() => toggle(perm.key)}
                              className="w-4 h-4 mt-0.5 text-indigo-600 rounded focus:ring-2 focus:ring-indigo-500 border-gray-300 shrink-0"
                            />
                            <div>
                              <p className={`text-sm font-semibold ${perms[perm.key] ? "text-indigo-900" : "text-gray-800"}`}>{perm.label}</p>
                              <p className="text-xs text-gray-500 mt-0.5">{perm.description}</p>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </TabsContent>
              </Tabs>
            </div>

            <div className="bg-gray-50 px-6 py-4 flex flex-col-reverse sm:flex-row sm:justify-end gap-3 border-t border-gray-200 shrink-0">
              <Button type="button" variant="outline" onClick={onClose} className="border-gray-300 text-gray-700">
                Cancel
              </Button>
              <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700 text-white">
                Save Changes
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
